import { useState, useEffect, useRef, useContext } from "react";
import Spinner from "react-bootstrap/Spinner";
import { Form, Row, Col } from "react-bootstrap";
import { formatDate } from "../../../utility/Formatdate";
import { RoleContext } from "../../../RoleContext";
import "../../../styles/typography.css";


// helper to format date with local timezone short name (e.g. IST)
function formatWithTimezone(value) {
  try {
    if (!value) return "";

    const utcDate = new Date(value);

    // Get the user's local timezone
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const options = {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
      timeZone: userTimeZone,
      timeZoneName: "long", // Gives IST, EST, PST, etc.
    };

    return utcDate.toLocaleString("en-US", options);
  } catch (e) {
    console.error("Error formatting date:", e);
    return String(value);
  }
}

function formatLogPerformedDate(value) {
  try {
    if (!value) return "";
    const strVal = String(value);

    let date;
    if (strVal.includes("Z") || /[+-]\d{2}:\d{2}$/.test(strVal)) {
      date = new Date(strVal);
    } else {
      const [datePart = "", timePart = "00:00:00"] = strVal.split("T");
      const [year, month, day] = datePart.split("-").map(Number);
      const [hour, min, sec] = timePart.split(":").map(Number);
      date = new Date(year, month - 1, day, hour || 0, min || 0, sec || 0);
    }

    if (Number.isNaN(date.getTime())) {
      return strVal;
    }

    // Always display in UTC to avoid local timezone offset shifting the time
    const formatted = date.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: "UTC",
    });

    const result = formatted.replace(",", "") + " UTC";
    return result;
  } catch {
    return String(value || "");
  }
}

// Converts a whole-number string to decimal 
const toDecimalString = (value) => {
  if (value === "" || value === null || value === undefined) return value;
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return Number.isInteger(num) && !String(value).includes(".")
    ? num.toFixed(1)
    : String(value);
};

const SampleEditPage = ({
  data,
  onDataChange,
  companyOptions,
  locationOptions,
  customerOptionsByLocation,
  productTypeOptions,
  speciesOptions,
  unitsOptions,
  availableProductTrades,
  availableFeedTypes,
  loadProductTypes,
  loadFeedTypes,
  loadSpecies,
  loadProductConfiguration,
  samples,
}) => {
  const [formData, setFormData] = useState({});
  const [companySearch, setCompanySearch] = useState("");
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
  const companyDropdownRef = useRef(null);

  const [status, setStatus] = useState("Received");

  const { role } = useContext(RoleContext);
  const isLabAdmin = role === "Lab Admin";

  // Lab Admin can edit sample details at any status.
  // Other roles are locked once the sample is Completed or Rejected.
  const isLocked =
    !isLabAdmin &&
    (formData.currentStatus === "Completed" ||
      formData.currentStatus === "Rejected");

  useEffect(() => {
    if (data) {
      const normalizedData = {
        ...data,
        companyId: data.companyId ?? data.companyID ?? "",
        locationId: data.locationId ?? data.oldLocationId ?? "",
        locationCustomerId:
          data.locationCustomerId ??
          data.customerId ??
          data.userId ??
          data.userID ??
          "",
      };

      setFormData(normalizedData);
      if (data.currentStatus) setStatus(data.currentStatus);
    }
  }, [data]);

  useEffect(() => {
    if (isCompanyDropdownOpen) return;

    const selectedCompany = (companyOptions || []).find(
      (company) => String(company.value) === String(formData.companyId || "")
    );
    setCompanySearch(selectedCompany?.label || "");
  }, [formData.companyId, companyOptions, isCompanyDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        companyDropdownRef.current &&
        !companyDropdownRef.current.contains(event.target)
      ) {
        setIsCompanyDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (field, value) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    onDataChange?.(updated);
  };

  const handleDropdownChange = (field, value) => {
    let updatedData = { ...formData, [field]: value };

    switch (field) {
      case "productTradeNameId":
        loadProductTypes(value);
        updatedData = { ...updatedData, productTypeId: "", feedTypeId: "", speciesId: "" };
        break;
      case "productTypeId":
        loadFeedTypes(formData.productTradeNameId, value);
        updatedData = { ...updatedData, feedTypeId: "", speciesId: "" };
        break;
      case "feedTypeId":
        loadSpecies(formData.productTradeNameId, formData.productTypeId, value);
        updatedData = { ...updatedData, speciesId: "" };
        break;
      case "speciesId":
        loadProductConfiguration(
          formData.productTradeNameId,
          formData.productTypeId,
          formData.feedTypeId,
          speciesOptions?.find((s) => s.value == value)?.masterSpeciesId
        );
        break;
      default:
        break;
    }

    setFormData(updatedData);
    onDataChange?.(updatedData);
  };

  const statuses = [
    "Requested",
    "Received",
    "Assay",
    "Re-Assay 1",
    "Re-Assay 2",
    "Results Available",
    "Approved",
    "Completed",
  ];

  if (!data) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
      <Spinner animation="border" /> Loading...
    </div>
  );

  return (
    <>
      <Row className="mb-3 justify-content-center">
        <Col xs={12} md={10} lg={8}>
          <div
            className="d-flex justify-content-center align-items-center gap-4 default-font-styles"
            style={{
              background: "#C7EDFF",
              borderRadius: 6,
              padding: "8px 0",
              fontSize: 16,
              fontWeight: 400,
            }}
          >
            <span style={{ color: "#003087" }}>
              Group ID: <b>{formData.groupId}</b>
            </span>
            <span style={{ color: "#003087" }}>
              Sample ID: <b>{formData.sampleId}</b>
            </span>
            <span style={{ color: "#003087" }}>
              Date:{" "}
              <b>{formData.date ? formatDate(new Date(formData.date)) : ""}</b>
            </span>
          </div>
        </Col>
      </Row>

      <Form>
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Company</Form.Label>
              <div className="position-relative" ref={companyDropdownRef}>
                <Form.Control
                  type="text"
                  placeholder="Select Company"
                  value={companySearch}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCompanySearch(value);
                    setIsCompanyDropdownOpen(true);

                    const updated = {
                      ...formData,
                      companyId: "",
                      companyID: "",
                      company: "",
                      locationId: "",
                      location: "",
                      locationCustomerId: "",
                    };

                    setFormData(updated);
                    onDataChange?.(updated);
                  }}
                  onFocus={() => setIsCompanyDropdownOpen(true)}
                  onBlur={() => {
                    setTimeout(() => {
                      const typed = (companySearch || "").trim();
                      if (!typed) {
                        setIsCompanyDropdownOpen(false);
                        return;
                      }

                      const exactMatch = (companyOptions || []).find(
                        (company) =>
                          (company.label || "").toLowerCase() === typed.toLowerCase()
                      );

                      if (exactMatch) {
                        const updated = {
                          ...formData,
                          companyId: exactMatch.value,
                          companyID: exactMatch.value,
                          company: exactMatch.label,
                          locationId: "",
                          location: "",
                          locationCustomerId: "",
                        };
                        setCompanySearch(exactMatch.label);
                        setFormData(updated);
                        onDataChange?.(updated);
                      }

                      setIsCompanyDropdownOpen(false);
                    }, 150);
                  }}
                  disabled={isLocked}
                />
                {isCompanyDropdownOpen && (
                  <div
                    className="dropdown-menu show w-100"
                    style={{ maxHeight: 240, overflowY: "auto" }}
                  >
                    {(companyOptions || [])
                      .filter((company) => {
                        const filter = (companySearch || "").toLowerCase();
                        if (!filter) return true;
                        return (company.label || "").toLowerCase().includes(filter);
                      })
                      .map((company) => (
                        <button
                          type="button"
                          key={company.value}
                          className="dropdown-item"
                          onClick={() => {
                            const updated = {
                              ...formData,
                              companyId: company.value,
                              companyID: company.value,
                              company: company.label,
                              locationId: "",
                              location: "",
                              locationCustomerId: "",
                            };

                            setCompanySearch(company.label);
                            setIsCompanyDropdownOpen(false);
                            setFormData(updated);
                            onDataChange?.(updated);
                          }}
                        >
                          {company.label}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Location</Form.Label>
              <Form.Select
                value={formData.locationId || ""}
                onChange={(e) => {
                  const selectedLocationId = e.target.value;
                  const selectedLocation = (locationOptions || []).find(
                    (location) => String(location.value) === String(selectedLocationId)
                  );

                  const updated = {
                    ...formData,
                    locationId: selectedLocationId,
                    locationID: selectedLocationId,
                    location: selectedLocation?.label || "",
                    locationCustomerId: "",
                    customerId: "",
                    customerName: "",
                  };

                  setFormData(updated);
                  onDataChange?.(updated);
                }}
                disabled={isLocked || !formData.companyId}
              >
                <option value="">Select Location</option>
                {(locationOptions || [])
                  .sort((a, b) => a.label.localeCompare(b.label))
                  .map((location) => (
                    <option key={location.value} value={location.value}>
                      {location.label}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Customer</Form.Label>
              <Form.Select
                value={formData.locationCustomerId || ""}
                onChange={(e) => {
                  const selectedCustomerId = e.target.value;
                  const selectedCustomer = (customerOptionsByLocation || []).find(
                    (customer) => String(customer.value) === String(selectedCustomerId)
                  );

                  const updated = {
                    ...formData,
                    locationCustomerId: selectedCustomerId,
                    customerId: selectedCustomerId,
                    customerName: selectedCustomer?.label || "",
                  };

                  setFormData(updated);
                  onDataChange?.(updated);
                }}
                disabled={isLocked || !formData.locationId}
              >
                <option value="">Select Customer</option>
                {(customerOptionsByLocation || []).map((customer) => (
                  <option key={customer.value} value={customer.value}>
                    {customer.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Product Trade Name</Form.Label>
              <select
                value={formData.productTradeNameId || ""}
                className="form-select"
                aria-label="Select Product Trade Name"
                onChange={(e) =>
                  handleDropdownChange("productTradeNameId", e.target.value)
                }
                disabled={isLocked}
              >
                {availableProductTrades
                  ?.map((trade) => ({
                    label: trade.productTradeName,
                    value: trade.productTradeNameId,
                  }))
                  .map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
              </select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Product Type</Form.Label>
              <select
                value={formData.productTypeId || ""}
                className="form-select"
                aria-label="Select Product Type"
                onChange={(e) =>
                  handleDropdownChange("productTypeId", e.target.value)
                }
                disabled={isLocked || formData.productTradeNameId === ""}
              >
                <option value="">Select Product Type</option>
                {productTypeOptions.map((type) => (
                  <option
                    key={type.value}
                    value={type.value}
                    label={type.label}
                    selected={type.value === formData.productTypeId}
                  ></option>
                ))}
              </select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Feed Type</Form.Label>
              <select
                value={formData.feedTypeId || ""}
                className="form-select"
                aria-label="Select Feed Type"
                onChange={(e) =>
                  handleDropdownChange("feedTypeId", e.target.value)
                }
                disabled={isLocked || formData.productTypeId === ""}
              >
                <option value="">Select Feed Type</option>
                {availableFeedTypes.map((type) => (
                  <option
                    key={type.value}
                    value={type.value}
                    label={type.label}
                    selected={type.value === formData.productTypeId}
                  >
                    {type.label}
                  </option>
                ))}
              </select>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Species</Form.Label>
              <select
                value={formData.speciesId || ""}
                className="form-select"
                aria-label="Default select example"
                disabled={isLocked || formData.feedTypeId === ""}
                onChange={(e) =>
                  handleDropdownChange("speciesId", e.target.value)
                }
              >
                <option value="">Select Species Type</option>
                {speciesOptions.map((type) => (
                  <option
                    key={type.value}
                    value={type.value}
                    label={type.label}
                    data-master-species-id={type.masterSpeciesId}
                    selected={type.value === formData.speciesId}
                  />
                ))}
              </select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Unit</Form.Label>
              <select
                className="form-select"
                aria-label="Default select example"
                value={formData.unitId || ""}
                disabled={isLocked}
                onChange={(e) => handleChange("unitId", e.target.value)}
              >
                {unitsOptions.map((type) => (
                  <option
                    key={type.value}
                    value={type.value}
                    label={type.label}
                    selected={type.value === formData.unitId}
                  />
                ))}
              </select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>CFR Min Level</Form.Label>
              <Form.Control
                value={formData.cfrMinLevel || ""}
                onChange={(e) => handleChange("cfrMinLevel", e.target.value)}
                disabled
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label>CFR Max Level</Form.Label>
              <Form.Control
                value={formData.cfrMaxLevel || ""}
                onChange={(e) => handleChange("cfrMaxLevel", e.target.value)}
                disabled
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>LOQ Value</Form.Label>
              <Form.Control
                value={formData.loqValue || ""}
                onChange={(e) => handleChange("loqValue", e.target.value)}
                disabled={true}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Expected Level</Form.Label>
              <Form.Control
                value={formData.expectedLevel || ""}
                disabled={isLocked}
                onChange={(e) => handleChange("expectedLevel", e.target.value)}
                onBlur={(e) => {
                  const converted = toDecimalString(e.target.value);
                  if (converted !== e.target.value) {
                    handleChange("expectedLevel", converted);
                  }
                }}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Result</Form.Label>
              <Form.Control
                value={formData.result || ""}
                onChange={(e) => handleChange("result", e.target.value)}
                disabled={true}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Phibro Lot No.</Form.Label>
              <Form.Control
                value={formData.lotNumber || ""}
                disabled={isLocked}
                onChange={(e) => handleChange("lotNumber", e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Customer Sample ID</Form.Label>
              <Form.Control
                value={formData.customerSampleId || ""}
                disabled={isLocked}
                onChange={(e) =>
                  handleChange("customerSampleId", e.target.value)
                }
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Reference No</Form.Label>
              <Form.Control
                value={formData.referenceNo || ""}
                disabled={isLocked}
                onChange={(e) =>
                  handleChange("referenceNo", e.target.value)
                }
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Manufacture Date</Form.Label>
              <Form.Control
                type="date"
                value={formData.manufactureDate ? formData.manufactureDate.split('T')[0] : ""}
                max={new Date().toISOString().split("T")[0]}
                onChange={(e) => handleChange("manufactureDate", e.target.value)}
                disabled={isLocked}
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Status Steps Row */}
        <div style={{ marginBottom: 10 }}>
          <div
            style={{ fontWeight: 500, marginBottom: 8 }}
            className="default-font-styles"
          >
            Current Status
          </div>
          <div
            className="status-steps-row default-font-styles"
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              justifyContent: "flex-start",
            }}
          >
            {statuses.map((s, idx) => {
              const currentIdx = statuses.indexOf(formData.currentStatus);
              let bg = "#626262";
              if (idx < currentIdx) bg = "#6DB33F"; // previous statuses
              if (idx === currentIdx) bg = "#003087"; // current status
              return (
                <div
                  key={s}
                  style={{
                    background: bg,
                    color: "#fff",
                    borderRadius: 50,
                    minWidth: 120,
                    height: 45,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow:
                      idx === currentIdx ? "0 0 0 2px #003087" : "none",
                    marginBottom: 8,
                    flex: "1 1 100px",
                    maxWidth: 130,
                  }}
                >
                  {s}
                </div>
              );
            })}
          </div>
        </div>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Comments</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={
                  Array.isArray(formData.sampleComments)
                    ? formData.sampleComments
                        .map((c) => c.comments + " UTC")
                        .join("\n\n")
                    : ""
                }
                disabled
                style={{ background: "#f5f5f5" }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.newComments || ""}
                onChange={(e) => handleChange("newComments", e.target.value)}
                placeholder="Add new comments here..."
                disabled={isLocked}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Label>Activity Log</Form.Label>
            <div
              style={{
                border: "1px solid #ccc",
                borderRadius: 5,
                padding: 10,
                height: 160,
                overflowY: "auto",
                background: "#f9f9f9",
              }}
            >
              {formData.activityLog?.length ? (
                formData.activityLog.map((log, idx) => {
                  const actionText = String(log.action || "");
                  const performedBy = String(log.performedBy || "").trim();
                  const requestedOnBehalfOf = String(
                    log.RequestedOnBehalfOf || log.requestedOnBehalfOf || ""
                  ).trim();
                  const isCreatedAction = actionText.toLowerCase().includes("created");
                  const showBehalfOf =
                    isCreatedAction &&
                    performedBy &&
                    requestedOnBehalfOf &&
                    performedBy.toLowerCase() !== requestedOnBehalfOf.toLowerCase();

                  return (
                    <div key={idx} className="mb-2">
                      <strong>
                        {showBehalfOf
                          ? `${actionText} by ${performedBy} behalf of ${requestedOnBehalfOf}`
                          : `${actionText} by ${performedBy}`}
                      </strong>{" "}
                      <br />
                      <small>
                        {formatLogPerformedDate(log.performedDate || log.performeddate)}
                      </small>{" "}
                      <br />
                      <small>{log.data}</small>
                    </div>
                  );
                })
              ) : (
                <div>No activity yet</div>
              )}
            </div>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default SampleEditPage;
